import { Card, Divider } from "@drone-js/core";
import { ContentPolicyAgreement } from "apps/batchOnboarding/contract/ContentPolicyAgreement";
import { VStack } from "apps/commons/components/Stack";
import { scrollToTop } from "apps/commons/functions/scrollToTop";
import { publishBinder } from "libs/analytics/binders/onboarding/publish";
import { useAnalytics } from "libs/analytics/useAnalytics";
import type { PropertyData } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { Title } from "../Title";
import { useOnboardingPageContextAtomValue } from "../onboardingPageContextAtom";
import { originSourceMapper } from "../originSourceMapper";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { usePropertyContext } from "../usePropertyContext";
import { AcceptTermsAndConditions } from "./AcceptTermsAndConditions";
import { ContractCheckboxController } from "./ContractCheckboxController";
import { InternalUserNotification } from "./InternalUserNotification";
import { Note } from "./Note";
import { PropertyOverview } from "./PropertyOverview";
import { TaxInformationController } from "./TaxInformationController";

import "./page.css";
import { LicenseController } from "./LicenseController";

export const ContractPage: FunctionComponent = () => {
    const {
        cmsData: {
            publish: { publish, taxInformationForCountry },
        },
    } = useStaticDataAtomValue();
    const { isInternalUser } = useOnboardingPageContextAtomValue();
    const propertyId = useWatch<PropertyData>({ name: "propertyId" }) as number;
    const onboardingId = useWatch<PropertyData>({ name: "onboardingId" }) as string;
    const countryName = useWatch<PropertyData>({ name: "country.name" }) as string;

    const { originSource, taxConfiguration, licenseConfiguration } = usePropertyContext(propertyId, onboardingId);

    useEffect(() => {
        scrollToTop();
    }, []);

    useAnalytics(publishBinder);

    const taxContent = (
        <TaxInformationController
            title={countryName ? taxInformationForCountry.replace("{0}", countryName) : undefined}
            {...taxConfiguration}
        />
    );

    const agreementContent = (
        <Fragment>
            <Divider />
            <AcceptTermsAndConditions checkbox={<ContractCheckboxController />} />
            {originSource === originSourceMapper.BcomImport && <ContentPolicyAgreement />}
        </Fragment>
    );

    const isLicenseRequired =
        licenseConfiguration?.requiredExemptible === true ||
        licenseConfiguration?.requiredLicenseId === true ||
        (licenseConfiguration?.licenseTypes?.length ?? 0) > 0 ||
        (licenseConfiguration?.documentUploadFieldIds?.length ?? 0) > 0;

    return (
        <VStack gap="24" pb="32">
            <Title title={publish} hideSaveAndExit />
            {isInternalUser && <InternalUserNotification />}
            <Card type="non-interactive" variant="filled" backgroundColor="base.inset" p="16">
                <VStack gap="16">
                    <PropertyOverview />
                    {licenseConfiguration && isLicenseRequired && (
                        <Fragment>
                            <Divider />
                            <LicenseController {...licenseConfiguration} />
                        </Fragment>
                    )}
                </VStack>
            </Card>
            {taxConfiguration && (
                <Card type="non-interactive" variant="filled" backgroundColor="base.inset" p="16">
                    {taxContent}
                </Card>
            )}
            {agreementContent}
            <Note />
        </VStack>
    );
};
